import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Grid, 
  Paper,
  TextField,
  Button,
  MenuItem,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { Link } from 'react-router-dom';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChatIcon from '@mui/icons-material/Chat';

// Animation
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components
const ContactContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(4, 0),
  animation: `${fadeIn} 0.5s ease-out`,
}));

const ContactHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
}));

const ContactInfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const ContactIcon = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.primary.main,
}));

// FAQ data
const faqData = [
  {
    question: 'How do I schedule an appointment?',
    answer: 'You can schedule an appointment through your dashboard by clicking on the "Schedule Appointment" button. You can choose your preferred doctor, date, and time based on availability.'
  },
  {
    question: 'How do video consultations work?',
    answer: 'Video consultations are conducted through our secure platform. At the scheduled time, log in to your account, go to your dashboard, and click on the "Start Video Call" button. Make sure your camera and microphone are working properly before the call.'
  },
  {
    question: 'What should I do if I need to cancel or reschedule an appointment?',
    answer: 'You can cancel or reschedule an appointment through your dashboard. Go to "My Appointments," find the appointment you want to change, and click on the "Cancel" or "Reschedule" button. Please note that cancellations should be made at least 24 hours in advance to avoid any fees.'
  },
  {
    question: 'How can I access my medical records?',
    answer: 'Your medical records are available in your dashboard under "Medical Records." You can view and download your records at any time. If you need specific records that are not available, please contact our support team.'
  },
  {
    question: 'Is my personal and medical information secure?',
    answer: 'Yes, we take data security very seriously. All your personal and medical information is encrypted and stored securely. We comply with all relevant healthcare privacy regulations, including HIPAA. You can review our privacy policy for more details.'
  }
];

const Contact = () => {
  // Form state
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'normal',
    attachment: null,
  });
  
  // Form submission state
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle file attachment
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('File size exceeds 5MB limit');
        return;
      }
      setFormData(prev => ({ ...prev, attachment: file }));
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (import.meta.env.DEV) console.log('Form submitted:', formData);
    // Here you would typically make an API call to send the message
    
    // Simulate successful submission
    setSubmitted(true);
    setFormData({
      subject: '',
      message: '',
      priority: 'normal',
      attachment: null,
    });
    
    // Reset file input
    const fileInput = document.getElementById('attachment-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };
  
  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSubmitted(false);
    setError(null);
  };
  
  return (
    <ContactContainer>
      <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
        <ContactHeader>
          <Button 
            component={Link} 
            to="/dashboard" 
            startIcon={<ArrowBackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Contact Us
          </Typography>
        </ContactHeader>
        
        <Grid container spacing={4}>
          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={1} 
              sx={{ 
                borderRadius: 2,
                p: 3,
                mb: { xs: 4, md: 0 },
              }}
            >
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Send Us a Message
              </Typography>
              
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      helperText="Please select the subject of your message"
                    >
                      <MenuItem value="technical">Technical Support</MenuItem>
                      <MenuItem value="billing">Billing Inquiry</MenuItem>
                      <MenuItem value="medical">Medical Question</MenuItem>
                      <MenuItem value="appointment">Appointment Issue</MenuItem>
                      <MenuItem value="feedback">Feedback</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      multiline
                      rows={6}
                      helperText="Please provide as much detail as possible"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Priority"
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      sx={{ height: '56px', width: '100%' }}
                    >
                      {formData.attachment ? formData.attachment.name : 'Attach File'}
                      <input
                        id="attachment-input"
                        type="file"
                        hidden
                        onChange={handleFileChange}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Max file size: 5MB
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        startIcon={<SendIcon />}
                        size="large"
                      >
                        Send Message
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
          
          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={1} 
              sx={{ 
                borderRadius: 2,
                p: 3,
                mb: 3,
              }}
            >
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Contact Information
              </Typography>
              
              <ContactInfoItem>
                <ContactIcon>
                  <EmailIcon />
                </ContactIcon>
                <Box>
                  <Typography variant="subtitle2">Email</Typography>
                  <Typography variant="body2" color="text.secondary">
                    support@alpha01med.com
                  </Typography>
                </Box>
              </ContactInfoItem>
              
              <ContactInfoItem>
                <ContactIcon>
                  <PhoneIcon />
                </ContactIcon>
                <Box>
                  <Typography variant="subtitle2">Phone</Typography>
                  <Typography variant="body2" color="text.secondary">
                    (555) 123-4567
                  </Typography>
                </Box>
              </ContactInfoItem>
              
              <ContactInfoItem>
                <ContactIcon>
                  <LocationOnIcon />
                </ContactIcon>
                <Box>
                  <Typography variant="subtitle2">Address</Typography>
                  <Typography variant="body2" color="text.secondary">
                    123 Medical Center Drive<br />
                    San Francisco, CA 94143
                  </Typography>
                </Box>
              </ContactInfoItem>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Hours of Operation
              </Typography>
              
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Monday - Friday
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    8:00 AM - 8:00 PM
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Saturday
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    9:00 AM - 5:00 PM
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Sunday
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    Closed
                  </Typography>
                </Grid>
              </Grid>
              
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<ChatIcon />}
                sx={{ mt: 3 }}
              >
                Start Live Chat
              </Button>
              <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
                Available during business hours
              </Typography>
            </Paper>
            
            <Paper 
              elevation={1} 
              sx={{ 
                borderRadius: 2,
                p: 3,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Response Time
              </Typography>
              <Typography variant="body2" paragraph>
                We strive to respond to all inquiries within 24 hours during business days.
              </Typography>
              <Typography variant="body2">
                For urgent matters, please call our support line or select "Urgent" priority when submitting your message.
              </Typography>
            </Paper>
          </Grid>
          
          {/* FAQ Section */}
          <Grid item xs={12}>
            <Paper 
              elevation={1} 
              sx={{ 
                borderRadius: 2,
                p: 3,
                mt: 4,
              }}
            >
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Frequently Asked Questions
              </Typography>
              
              {faqData.map((faq, index) => (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`faq-content-${index}`}
                    id={`faq-header-${index}`}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
              
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Can't find what you're looking for?
                </Typography>
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Contact Us
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      
      {/* Success/Error Snackbar */}
      <Snackbar
        open={submitted || !!error}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={error ? 'error' : 'success'} 
          sx={{ width: '100%' }}
        >
          {error ? error : 'Your message has been sent successfully. We will get back to you soon.'}
        </Alert>
      </Snackbar>
    </ContactContainer>
  );
};

export default Contact;
